/**
 * WeekGeneratorService - Génère automatiquement une semaine de tâches
 * Basé sur les templates et les préférences utilisateur
 */

const STORAGE_KEY = 'adhd_planner_advanced_settings';

// Templates de journée type
const DAY_TEMPLATES = {
  minimal: {
    name: 'Minimal',
    description: 'Essentiels uniquement - pour les jours difficiles',
    tasks: [
      { time: 'wake', offset: 0, task: 'Médicaments', duration: 5, category: 'vital', icon: '💊' },
      { time: 'wake', offset: 30, task: 'Petit-déjeuner', duration: 20, category: 'vital', icon: '🍳' },
      { time: 'lunch', offset: 0, task: 'Déjeuner', duration: 30, category: 'vital', icon: '🍽️' },
      { time: 'dinner', offset: 0, task: 'Dîner', duration: 30, category: 'vital', icon: '🍲' },
      { time: 'sleep', offset: -30, task: 'Routine du soir', duration: 20, category: 'vital', icon: '🌙' }
    ]
  },
  balanced: {
    name: 'Équilibré',
    description: 'Mix sain entre essentiels et productivité',
    tasks: [
      { time: 'wake', offset: 0, task: 'Médicaments', duration: 5, category: 'vital', icon: '💊' },
      { time: 'wake', offset: 15, task: 'Routine matinale', duration: 30, category: 'vital', icon: '🚿' },
      { time: 'breakfast', offset: 0, task: 'Petit-déjeuner', duration: 20, category: 'vital', icon: '🍳' },
      { time: 'workStart', offset: 0, task: 'Tâche importante', duration: 45, category: 'chore', icon: '📋' },
      { time: 'workStart', offset: 60, task: 'Pause café', duration: 15, category: 'pleasure', icon: '☕' },
      { time: 'lunch', offset: 0, task: 'Déjeuner', duration: 45, category: 'vital', icon: '🍽️' },
      { time: 'lunch', offset: 60, task: 'Micro-sieste/repos', duration: 20, category: 'pleasure', icon: '😴' },
      { time: 'workStart', offset: 240, task: 'Tâche secondaire', duration: 30, category: 'chore', icon: '📝' },
      { time: 'workEnd', offset: 30, task: 'Marche/Sport léger', duration: 30, category: 'pleasure', icon: '🚶' },
      { time: 'dinner', offset: 0, task: 'Préparer le dîner', duration: 30, category: 'chore', icon: '👨‍🍳' },
      { time: 'dinner', offset: 30, task: 'Dîner', duration: 30, category: 'vital', icon: '🍲' },
      { time: 'sleep', offset: -60, task: 'Temps libre', duration: 30, category: 'reward', icon: '🎮' },
      { time: 'sleep', offset: -30, task: 'Routine du soir', duration: 20, category: 'vital', icon: '🌙' }
    ]
  },
  structured: {
    name: 'Structuré',
    description: 'Journée complète et organisée',
    tasks: [
      { time: 'wake', offset: 0, task: 'Réveil + Médicaments', duration: 5, category: 'vital', icon: '💊' },
      { time: 'wake', offset: 10, task: 'Étirements matinaux', duration: 10, category: 'vital', icon: '🧘' },
      { time: 'wake', offset: 25, task: 'Douche + Hygiène', duration: 20, category: 'vital', icon: '🚿' },
      { time: 'breakfast', offset: 0, task: 'Petit-déjeuner complet', duration: 25, category: 'vital', icon: '🍳' },
      { time: 'breakfast', offset: 30, task: 'Ranger la cuisine', duration: 10, category: 'chore', icon: '🧹' },
      { time: 'workStart', offset: 0, task: 'Tâche prioritaire #1', duration: 50, category: 'chore', icon: '🎯' },
      { time: 'workStart', offset: 55, task: 'Pause active', duration: 10, category: 'pleasure', icon: '🚶' },
      { time: 'workStart', offset: 70, task: 'Tâche prioritaire #2', duration: 50, category: 'chore', icon: '📋' },
      { time: 'lunch', offset: -15, task: 'Pause avant déjeuner', duration: 10, category: 'pleasure', icon: '☕' },
      { time: 'lunch', offset: 0, task: 'Préparer déjeuner', duration: 20, category: 'chore', icon: '👨‍🍳' },
      { time: 'lunch', offset: 20, task: 'Déjeuner', duration: 30, category: 'vital', icon: '🍽️' },
      { time: 'lunch', offset: 55, task: 'Marche digestive', duration: 15, category: 'pleasure', icon: '🚶' },
      { time: 'lunch', offset: 75, task: 'Tâche légère', duration: 30, category: 'chore', icon: '📝' },
      { time: 'workStart', offset: 300, task: 'Tâche créative', duration: 45, category: 'chore', icon: '✨' },
      { time: 'workEnd', offset: 0, task: 'Ranger l\'espace de travail', duration: 10, category: 'chore', icon: '🧹' },
      { time: 'workEnd', offset: 15, task: 'Sport/Activité physique', duration: 45, category: 'pleasure', icon: '🏃' },
      { time: 'dinner', offset: -30, task: 'Douche', duration: 15, category: 'vital', icon: '🚿' },
      { time: 'dinner', offset: 0, task: 'Préparer le dîner', duration: 30, category: 'chore', icon: '👨‍🍳' },
      { time: 'dinner', offset: 30, task: 'Dîner', duration: 30, category: 'vital', icon: '🍲' },
      { time: 'dinner', offset: 65, task: 'Vaisselle', duration: 15, category: 'chore', icon: '🍽️' },
      { time: 'sleep', offset: -90, task: 'Temps libre mérité', duration: 45, category: 'reward', icon: '🎮' },
      { time: 'sleep', offset: -45, task: 'Préparation lendemain', duration: 10, category: 'chore', icon: '📋' },
      { time: 'sleep', offset: -30, task: 'Routine du soir', duration: 20, category: 'vital', icon: '🌙' },
      { time: 'sleep', offset: -10, task: 'Méditation/Lecture', duration: 10, category: 'pleasure', icon: '📖' }
    ]
  },
  intense: {
    name: 'Intense',
    description: 'Productivité maximale - pour les jours motivés',
    tasks: [
      { time: 'wake', offset: 0, task: 'Réveil + Médicaments', duration: 5, category: 'vital', icon: '💊' },
      { time: 'wake', offset: 5, task: 'Sport matinal', duration: 30, category: 'vital', icon: '🏃' },
      { time: 'wake', offset: 40, task: 'Douche froide', duration: 10, category: 'vital', icon: '🚿' },
      { time: 'breakfast', offset: 0, task: 'Petit-déjeuner protéiné', duration: 20, category: 'vital', icon: '🍳' },
      { time: 'workStart', offset: -30, task: 'Planification journée', duration: 15, category: 'chore', icon: '📋' },
      { time: 'workStart', offset: 0, task: 'Deep Work #1', duration: 90, category: 'chore', icon: '🎯' },
      { time: 'workStart', offset: 95, task: 'Pause Pomodoro', duration: 15, category: 'pleasure', icon: '☕' },
      { time: 'workStart', offset: 115, task: 'Deep Work #2', duration: 60, category: 'chore', icon: '💻' },
      { time: 'lunch', offset: 0, task: 'Déjeuner rapide', duration: 30, category: 'vital', icon: '🍽️' },
      { time: 'lunch', offset: 35, task: 'Marche active', duration: 20, category: 'pleasure', icon: '🚶' },
      { time: 'lunch', offset: 60, task: 'Deep Work #3', duration: 60, category: 'chore', icon: '📊' },
      { time: 'lunch', offset: 125, task: 'Tâches admin rapides', duration: 30, category: 'chore', icon: '📧' },
      { time: 'lunch', offset: 160, task: 'Deep Work #4', duration: 60, category: 'chore', icon: '✨' },
      { time: 'workEnd', offset: 0, task: 'Revue de journée', duration: 15, category: 'chore', icon: '📋' },
      { time: 'workEnd', offset: 20, task: 'Sport/Gym', duration: 60, category: 'pleasure', icon: '🏋️' },
      { time: 'dinner', offset: 0, task: 'Dîner', duration: 30, category: 'vital', icon: '🍲' },
      { time: 'sleep', offset: -90, task: 'Projet personnel', duration: 45, category: 'pleasure', icon: '🎨' },
      { time: 'sleep', offset: -40, task: 'Lecture/Apprentissage', duration: 20, category: 'reward', icon: '📖' },
      { time: 'sleep', offset: -15, task: 'Routine sommeil', duration: 10, category: 'vital', icon: '🌙' }
    ]
  }
};

// Templates pour weekends
const WEEKEND_TEMPLATES = {
  minimal: {
    tasks: [
      { time: 'wake', offset: 60, task: 'Grasse matinée + Médicaments', duration: 10, category: 'vital', icon: '💊' },
      { time: 'wake', offset: 90, task: 'Brunch', duration: 40, category: 'pleasure', icon: '🍳' },
      { time: 'lunch', offset: 120, task: 'Repos', duration: 120, category: 'pleasure', icon: '😴' },
      { time: 'dinner', offset: 0, task: 'Dîner', duration: 30, category: 'vital', icon: '🍲' }
    ]
  },
  balanced: {
    tasks: [
      { time: 'wake', offset: 60, task: 'Réveil tranquille + Médicaments', duration: 10, category: 'vital', icon: '💊' },
      { time: 'wake', offset: 90, task: 'Brunch', duration: 45, category: 'pleasure', icon: '🍳' },
      { time: 'wake', offset: 150, task: 'Ménage léger', duration: 30, category: 'chore', icon: '🧹' },
      { time: 'lunch', offset: 60, task: 'Sortie/Balade', duration: 90, category: 'pleasure', icon: '🚶' },
      { time: 'dinner', offset: -60, task: 'Loisir créatif', duration: 60, category: 'pleasure', icon: '🎨' },
      { time: 'dinner', offset: 0, task: 'Dîner', duration: 30, category: 'vital', icon: '🍲' },
      { time: 'sleep', offset: -120, task: 'Film/Série', duration: 90, category: 'reward', icon: '🎬' }
    ]
  },
  structured: {
    tasks: [
      { time: 'wake', offset: 30, task: 'Réveil + Médicaments', duration: 10, category: 'vital', icon: '💊' },
      { time: 'wake', offset: 60, task: 'Sport matinal', duration: 45, category: 'pleasure', icon: '🏃' },
      { time: 'breakfast', offset: 30, task: 'Brunch copieux', duration: 45, category: 'pleasure', icon: '🍳' },
      { time: 'breakfast', offset: 90, task: 'Grand ménage', duration: 90, category: 'chore', icon: '🧹' },
      { time: 'lunch', offset: 60, task: 'Courses de la semaine', duration: 60, category: 'chore', icon: '🛒' },
      { time: 'lunch', offset: 150, task: 'Loisir/Hobby', duration: 90, category: 'pleasure', icon: '🎨' },
      { time: 'dinner', offset: -30, task: 'Cuisine élaborée', duration: 60, category: 'chore', icon: '👨‍🍳' },
      { time: 'dinner', offset: 30, task: 'Dîner', duration: 45, category: 'vital', icon: '🍲' },
      { time: 'sleep', offset: -120, task: 'Soirée détente', duration: 90, category: 'reward', icon: '🎬' },
      { time: 'sleep', offset: -30, task: 'Préparer la semaine', duration: 20, category: 'chore', icon: '📋' }
    ]
  },
  intense: {
    tasks: [
      { time: 'wake', offset: 30, task: 'Réveil + Médicaments', duration: 10, category: 'vital', icon: '💊' },
      { time: 'wake', offset: 45, task: 'Sport longue durée', duration: 90, category: 'pleasure', icon: '🏃' },
      { time: 'breakfast', offset: 30, task: 'Brunch healthy', duration: 40, category: 'vital', icon: '🥗' },
      { time: 'breakfast', offset: 90, task: 'Projet personnel #1', duration: 120, category: 'chore', icon: '💻' },
      { time: 'lunch', offset: 60, task: 'Déjeuner', duration: 30, category: 'vital', icon: '🍽️' },
      { time: 'lunch', offset: 100, task: 'Ménage complet', duration: 120, category: 'chore', icon: '🧹' },
      { time: 'lunch', offset: 240, task: 'Projet personnel #2', duration: 90, category: 'chore', icon: '🎯' },
      { time: 'dinner', offset: 0, task: 'Dîner', duration: 30, category: 'vital', icon: '🍲' },
      { time: 'sleep', offset: -90, task: 'Récompense méritée', duration: 60, category: 'reward', icon: '🎬' }
    ]
  }
};

// Tâches d'hygiène additionnelles
const HYGIENE_TASKS = [
  { task: 'Se brosser les dents (matin)', time: 'wake', offset: 10, duration: 3, category: 'vital', icon: '🦷' },
  { task: 'Se brosser les dents (soir)', time: 'sleep', offset: -20, duration: 3, category: 'vital', icon: '🦷' },
  { task: 'Skincare matin', time: 'wake', offset: 45, duration: 5, category: 'vital', icon: '🧴' },
  { task: 'Skincare soir', time: 'sleep', offset: -25, duration: 5, category: 'vital', icon: '🧴' }
];

// Tâches de pause additionnelles
const BREAK_TASKS = [
  { task: 'Pause respirations', offset: 120, duration: 5, category: 'pleasure', icon: '🧘' },
  { task: 'Pause étirements', offset: 180, duration: 5, category: 'pleasure', icon: '🤸' },
  { task: 'Pause hydratation', offset: 90, duration: 2, category: 'vital', icon: '💧' }
];

class WeekGeneratorService {
  constructor() {
    this.settings = null;
  }

  loadSettings() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.settings = JSON.parse(stored);
      }
    } catch (e) {
      console.warn('Erreur chargement settings:', e);
    }
    return this.settings;
  }

  /**
   * Convertir un temps de référence en minutes depuis minuit
   */
  timeToMinutes(timeStr) {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  }

  /**
   * Convertir des minutes depuis minuit en string HH:MM
   */
  minutesToTime(minutes) {
    const h = Math.floor(minutes / 60) % 24;
    const m = minutes % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }

  /**
   * Obtenir le temps réel pour une référence de temps
   */
  getTimeReference(ref, schedule) {
    switch (ref) {
      case 'wake': return this.timeToMinutes(schedule.wakeTime || '07:00');
      case 'sleep': return this.timeToMinutes(schedule.sleepTime || '23:00');
      case 'breakfast': return this.timeToMinutes(schedule.breakfastTime || '08:00');
      case 'lunch': return this.timeToMinutes(schedule.lunchTime || '12:30');
      case 'dinner': return this.timeToMinutes(schedule.dinnerTime || '19:30');
      case 'workStart': return this.timeToMinutes(schedule.workStartTime || '09:00');
      case 'workEnd': return this.timeToMinutes(schedule.workEndTime || '18:00');
      default: return this.timeToMinutes('12:00');
    }
  }

  /**
   * Générer un ID unique pour une tâche
   */
  generateTaskId() {
    return `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Générer les tâches pour un jour donné
   */
  generateDayTasks(date, isWeekend, options = {}) {
    this.loadSettings();

    const schedule = this.settings?.schedule || {
      wakeTime: '07:00',
      sleepTime: '23:00',
      breakfastTime: '08:00',
      lunchTime: '12:30',
      dinnerTime: '19:30',
      workStartTime: '09:00',
      workEndTime: '18:00'
    };

    const template = options.template || this.settings?.autoGeneration?.template || 'balanced';
    const dayTemplate = isWeekend
      ? WEEKEND_TEMPLATES[template] || WEEKEND_TEMPLATES.balanced
      : DAY_TEMPLATES[template] || DAY_TEMPLATES.balanced;

    const tasks = [];
    const usedSlots = new Set();

    // Générer les tâches du template
    dayTemplate.tasks.forEach(taskDef => {
      const baseMinutes = this.getTimeReference(taskDef.time, schedule);
      let taskMinutes = baseMinutes + (taskDef.offset || 0);

      // S'assurer que l'heure est dans les limites
      if (taskMinutes < 0) taskMinutes += 24 * 60;
      if (taskMinutes >= 24 * 60) taskMinutes -= 24 * 60;

      const timeStr = this.minutesToTime(taskMinutes);

      // Éviter les doublons au même créneau
      if (!usedSlots.has(timeStr)) {
        usedSlots.add(timeStr);

        tasks.push({
          id: this.generateTaskId(),
          task: taskDef.task,
          time: timeStr,
          duration: taskDef.duration,
          category: taskDef.category,
          iconName: this.getIconName(taskDef.category),
          date: date,
          isCustom: true,
          isGenerated: true,
          instructions: [],
          emoji: taskDef.icon
        });
      }
    });

    // Ajouter les tâches d'hygiène si activées
    if (options.autoAddHygiene !== false) {
      HYGIENE_TASKS.forEach(taskDef => {
        const baseMinutes = this.getTimeReference(taskDef.time, schedule);
        let taskMinutes = baseMinutes + (taskDef.offset || 0);
        if (taskMinutes < 0) taskMinutes += 24 * 60;
        if (taskMinutes >= 24 * 60) taskMinutes -= 24 * 60;

        const timeStr = this.minutesToTime(taskMinutes);

        if (!usedSlots.has(timeStr)) {
          usedSlots.add(timeStr);

          tasks.push({
            id: this.generateTaskId(),
            task: taskDef.task,
            time: timeStr,
            duration: taskDef.duration,
            category: taskDef.category,
            iconName: this.getIconName(taskDef.category),
            date: date,
            isCustom: true,
            isGenerated: true,
            instructions: [],
            emoji: taskDef.icon
          });
        }
      });
    }

    // Ajouter les pauses si activées (uniquement jours de travail)
    if (options.autoAddBreaks !== false && !isWeekend) {
      const workStart = this.getTimeReference('workStart', schedule);

      BREAK_TASKS.forEach(taskDef => {
        let taskMinutes = workStart + taskDef.offset;
        if (taskMinutes >= 24 * 60) taskMinutes -= 24 * 60;

        const timeStr = this.minutesToTime(taskMinutes);

        tasks.push({
          id: this.generateTaskId(),
          task: taskDef.task,
          time: timeStr,
          duration: taskDef.duration,
          category: taskDef.category,
          iconName: this.getIconName(taskDef.category),
          date: date,
          isCustom: true,
          isGenerated: true,
          instructions: [],
          emoji: taskDef.icon
        });
      });
    }

    // Trier par heure
    tasks.sort((a, b) => {
      const timeA = this.timeToMinutes(a.time);
      const timeB = this.timeToMinutes(b.time);
      return timeA - timeB;
    });

    return tasks;
  }

  /**
   * Générer une semaine complète
   */
  generateWeek(startDate = new Date(), options = {}) {
    this.loadSettings();

    const autoGenSettings = this.settings?.autoGeneration || {
      template: 'balanced',
      includeWeekends: true,
      autoAddMeals: true,
      autoAddHygiene: true,
      autoAddBreaks: true
    };

    // Fusionner les options avec les settings
    const finalOptions = { ...autoGenSettings, ...options };

    const weekTasks = {};
    const start = new Date(startDate);

    // Trouver le lundi de la semaine
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    start.setDate(diff);

    // Générer pour chaque jour
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(start);
      currentDate.setDate(start.getDate() + i);

      const isWeekend = i >= 5; // Samedi (5) et Dimanche (6)
      const dateStr = currentDate.toISOString().split('T')[0];

      // Si weekends non inclus, skip
      if (isWeekend && !finalOptions.includeWeekends) {
        weekTasks[dateStr] = [];
        continue;
      }

      weekTasks[dateStr] = this.generateDayTasks(dateStr, isWeekend, finalOptions);
    }

    return weekTasks;
  }

  /**
   * Obtenir le nom d'icône pour une catégorie
   */
  getIconName(category) {
    const icons = {
      vital: 'AlertCircle',
      chore: 'Briefcase',
      pleasure: 'Coffee',
      reward: 'Trophy'
    };
    return icons[category] || 'ListTodo';
  }

  /**
   * Obtenir les templates disponibles
   */
  getAvailableTemplates() {
    return Object.entries(DAY_TEMPLATES).map(([key, template]) => ({
      key,
      name: template.name,
      description: template.description,
      taskCount: template.tasks.length
    }));
  }

  /**
   * Prévisualiser une journée avec un template
   */
  previewDay(template, isWeekend = false) {
    this.loadSettings();

    const schedule = this.settings?.schedule || {
      wakeTime: '07:00',
      sleepTime: '23:00',
      breakfastTime: '08:00',
      lunchTime: '12:30',
      dinnerTime: '19:30',
      workStartTime: '09:00',
      workEndTime: '18:00'
    };

    const dayTemplate = isWeekend
      ? WEEKEND_TEMPLATES[template] || WEEKEND_TEMPLATES.balanced
      : DAY_TEMPLATES[template] || DAY_TEMPLATES.balanced;

    return dayTemplate.tasks.map(taskDef => {
      const baseMinutes = this.getTimeReference(taskDef.time, schedule);
      let taskMinutes = baseMinutes + (taskDef.offset || 0);
      if (taskMinutes < 0) taskMinutes += 24 * 60;
      if (taskMinutes >= 24 * 60) taskMinutes -= 24 * 60;

      return {
        time: this.minutesToTime(taskMinutes),
        task: taskDef.task,
        duration: taskDef.duration,
        category: taskDef.category,
        emoji: taskDef.icon
      };
    }).sort((a, b) => this.timeToMinutes(a.time) - this.timeToMinutes(b.time));
  }
}

const weekGeneratorService = new WeekGeneratorService();
export default weekGeneratorService;

export { DAY_TEMPLATES, WEEKEND_TEMPLATES };
